import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import {
	LOGIN_MUTATION,
	LOGOUT_MUTATION,
	CURRENT_USER_QUERY
} from '@/pages/auth/auth.gql';
import type {
	LoginMutation,
	LoginMutationVariables,
	LogoutMutation,
	CurrentUserQuery
} from '@/graphql/generated';
import {
	setAuthErrorCallback,
	clearAuthErrorCallback,
	apolloClient
} from '@/lib/apolloClient';

type AuthUser = {
	userId: string;
	username: string;
	email: string;
	roles: string[];
	facilities: Array<{
		facilityId: string;
		name: string;
	}>;
};

interface AuthContextValue {
	user: AuthUser | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	login: (credentials: {
		username: string;
		password: string;
	}) => Promise<void>;
	logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
	children
}) => {
	const [user, setUser] = useState<AuthUser | null>(null);
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(true);

	// GraphQL mutations and queries
	const [loginMutation] = useMutation<LoginMutation, LoginMutationVariables>(
		LOGIN_MUTATION
	);
	const [logoutMutation] = useMutation<LogoutMutation>(LOGOUT_MUTATION);
	const [getCurrentUser] = useLazyQuery<CurrentUserQuery>(
		CURRENT_USER_QUERY,
		{
			fetchPolicy: 'network-only' // Always fetch from server for auth state
		}
	);

	// Handle authentication errors from GraphQL
	// Note: Using window.location because AuthProvider is outside Router context
	const handleAuthError = useCallback(() => {
		console.warn('[Auth] Session expired or invalid, clearing auth state');
		setUser(null);
		setIsAuthenticated(false);
		// Clear Apollo cache to remove any cached authenticated data
		apolloClient.clearStore();
		// Redirect to login if not already there
		// Using window.location because this runs outside Router context
		if (window.location.pathname !== '/login') {
			window.location.href = '/login';
		}
	}, []);

	// Register auth error callback with Apollo client
	useEffect(() => {
		setAuthErrorCallback(handleAuthError);
		return () => {
			clearAuthErrorCallback();
		};
	}, [handleAuthError]);

	// Check for existing session on mount
	useEffect(() => {
		const checkSession = async () => {
			try {
				const { data } = await getCurrentUser();

				if (data?.currentUser?.isAuthenticated && data.currentUser.user) {
					const userData = data.currentUser.user;
					setUser({
						userId: userData.userId || '',
						username: userData.username || '',
						email: userData.email || '',
						roles: userData.roles || [],
						facilities: userData.facilities || []
					});
					setIsAuthenticated(true);
				} else {
					setUser(null);
					setIsAuthenticated(false);
				}
			} catch (error) {
				console.error('Failed to check session:', error);
				setUser(null);
				setIsAuthenticated(false);
			} finally {
				setIsLoading(false);
			}
		};

		checkSession();
	}, [getCurrentUser]);

	const login = useCallback(
		async ({
			username,
			password
		}: {
			username: string;
			password: string;
		}) => {
			if (!username || !password) {
				throw new Error('Username and password are required');
			}

			try {
				const { data } = await loginMutation({
					variables: { username, password }
				});

				if (data?.login?.success && data.login.user) {
					const userData = data.login.user;
					setUser({
						userId: userData.userId || '',
						username: userData.username || '',
						email: userData.email || '',
						roles: userData.roles || [],
						facilities: userData.facilities || []
					});
					setIsAuthenticated(true);
				} else {
					throw new Error(data?.login?.error || 'Login failed');
				}
			} catch (error) {
				console.error('Login error:', error);
				throw error;
			}
		},
		[loginMutation]
	);

	const logout = useCallback(async () => {
		try {
			await logoutMutation();
		} catch (error) {
			console.error('Logout error:', error);
		} finally {
			// Always clear local state, even if mutation fails
			setUser(null);
			setIsAuthenticated(false);
		}
	}, [logoutMutation]);

	const value = useMemo<AuthContextValue>(
		() => ({ user, isAuthenticated, isLoading, login, logout }),
		[user, isAuthenticated, isLoading, login, logout]
	);

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);

	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}

	return context;
};
